import {
  ButtonInteractionState,
  IconButton,
} from '@droplet-tech-code/core-elements/module/ui/Button';
import { IconCircle } from '@droplet-tech-code/core-elements/module/ui/Icon';
import {
  useModal,
  useModalVerify,
} from '@droplet-tech-code/core-elements/module/ui/Modal';
import {
  PopoverContainer,
  popoverControl,
  PopoverList,
} from '@droplet-tech-code/core-elements/module/ui/Popover';
import { HStack } from '@droplet-tech-code/core-elements/module/ui/Stack';
import { Text } from '@droplet-tech-code/core-elements/module/ui/Text';
import {
  isNativeMobile,
  isWeb,
} from '@droplet-tech-code/core-elements/module/utils/utils.helper';
import { SupportRequestBackendClient } from '@naus-code/naus-admin-types';
import { useEffect, useState } from 'react';
import { Pressable, TouchableOpacity } from 'react-native';

import { LiveDateText } from '~/components/DateText';
import { LinkText } from '~/components/LinkText';
import { MenuActionItem } from '~/components/MenuActionItem';
import { PageHeader } from '~/components/Page/Page.Header';
import { Pill } from '~/components/Pill/Pill';
import { SwitchStack } from '~/components/Stack/SwitchStack';
import { useSupportDetailsModal } from '~/components/SupportRequest/MobileSupportPanel';
import { useChangeSupportType } from '~/components/SupportRequest/Modals/ChangeSupportType';
import { AppText } from '~/components/Text';
import { useAppNavigation } from '~/hooks/nav.hook';
import { useGetAuthUser } from '~/screens/Auth/Auth.Context';
import { supportRequestApi } from '~/screens/Support/Requests/SupportRequests.api';
import { translate } from '~/utils/translation.utils';

import { supportRequestActionsApi } from './supportRequestActions.api';
import { useConversionTool } from '~/components/CurrencyConversion/CurrencyConversionTool';
import { useDispatch } from 'react-redux';
import { purchaseApi } from '../Purchases/Purchases.api';
import { bookingApi } from '../Bookings/Bookings.api';
import { useGetSupportItemAdminUser } from './utils/supportRequest.utils';
import { notEmpty } from '@naus-code/naus-basket-manager/src/utils';
import { dayjs } from '@droplet-tech-code/core-elements/module/utils/utils.date';
import {
  getVIOptionsFromSR,
  useProvisionalPaymentValidUntil,
} from '~/components/SupportRequest/utils/SupportRequestData';

export const SupportRequestItemHeader = ({
  supportRequest,
  check,
  onClose,
}: {
  supportRequest: SupportRequestBackendClient;
  check: string;
  onClose?: () => void;
}) => {
  const dispatch = useDispatch();
  const { closeModal } = useModal();
  const [deleteRequest, { isLoading: isDeleting }] =
    supportRequestApi.useDeleteSupportRequestMutation();
  const { refetch, isFetching } = supportRequestApi.useGetSupportRequestQuery(
    supportRequest._id,
  );

  const onRefetch = () => {
    if (isNativeMobile) {
      return;
    }
    refetch();
    dispatch(purchaseApi.util.invalidateTags(['getPurchase']));
    dispatch(bookingApi.util.invalidateTags(['getBooking']));
  };

  const { verifyAction } = useModalVerify();
  const { open: openChangeType } = useChangeSupportType();
  const { open: openDetails } = useSupportDetailsModal();
  const { open: openConversionTool } = useConversionTool();

  if (supportRequest === undefined) {
    return null;
  }

  const { p_email, ref, dateCreated, supportTypeTitle } = supportRequest;
  return (
    <PageHeader
      title={supportTypeTitle}
      withBack={isNativeMobile}
      iconLeft={{
        color: 'monochrome',
        icon: 'close',
        onPress: onClose,
      }}
      childRight={
        <HStack space="3" flex={1} align="center">
          <PendingBookingExpiryPill supportRequest={supportRequest} />
          <AssignSelfButton supportRequest={supportRequest} check={check} />
          <IconButton
            color="monochrome"
            icon="refresh"
            onPress={onRefetch}
            isLoading={isFetching}
          />
          <PopoverContainer
            width={250}
            maxHeight={320}
            offsetX={-5}
            Popover={
              <PopoverList
                list={[
                  {
                    item: (
                      <TouchableOpacity
                        onPress={async () => {
                          closeModal('openPopover');
                          popoverControl.closePopover();
                          await verifyAction({
                            title: supportRequest.ref,
                            description: translate('supportRequests.areYouSureDelete'),
                          }).then(async (verify) => {
                            if (verify) {
                              await deleteRequest(supportRequest._id);
                            }
                          });
                        }}
                      >
                        <DeleteButton isLoading={isDeleting} />
                      </TouchableOpacity>
                    ),
                  },
                  {
                    item: (
                      <TouchableOpacity
                        onPress={async () => {
                          closeModal('openPopover');
                          popoverControl.closePopover();
                          openChangeType(check, supportRequest);
                        }}
                      >
                        <ChangeTypeButton />
                      </TouchableOpacity>
                    ),
                  },
                  {
                    item: (
                      <TouchableOpacity
                        onPress={async () => {
                          closeModal('openPopover');
                          popoverControl.closePopover();
                          openConversionTool({});
                        }}
                      >
                        <ConversionTool />
                      </TouchableOpacity>
                    ),
                  },
                  ...(isNativeMobile
                    ? [
                        {
                          item: (
                            <TouchableOpacity
                              onPress={async () => {
                                closeModal('openPopover');
                                popoverControl.closePopover();
                                openDetails(supportRequest._id);
                              }}
                            >
                              <DetailsButton />
                            </TouchableOpacity>
                          ),
                        },
                      ]
                    : []),
                ]}
              />
            }
          >
            {({ hovered }: ButtonInteractionState) => {
              {
                return <IconCircle icon="menu" hovered={hovered} />;
              }
            }}
          </PopoverContainer>
        </HStack>
      }
      Subtitle={
        isWeb ? (
          <SupportRequestItemHeaderSubTitle
            email={p_email}
            reference={ref}
            dateCreated={dateCreated}
            zendeskTicketNumber={supportRequest.zdData?.ticketId}
            trackId={
              supportRequest.supportTypeId === 'tracking-report' && supportRequest.trackId
                ? supportRequest.trackId
                : undefined
            }
          />
        ) : null
      }
    />
  );
};

const PendingBookingExpiryPill = ({
  supportRequest,
}: {
  supportRequest: SupportRequestBackendClient;
}) => {
  const provisionalValidUntil = useProvisionalPaymentValidUntil(supportRequest);
  if (provisionalValidUntil) {
    return <ProvisionalExpiryPill validUntil={provisionalValidUntil} />;
  }

  const viValidUntil = getVIOptionsFromSR(supportRequest)?.validUntil;
  if (!viValidUntil || !dayjs(viValidUntil).isBefore(dayjs())) {
    return null;
  }

  return (
    <Pill color="error" bg="error-extraLight">
      <AppText.Body2Medium color="error">
        {translate('supportRequests.virtualInventory.expired')}
      </AppText.Body2Medium>
    </Pill>
  );
};

const PROVISIONAL_EXPIRY_WARNING_MINUTES = 2 * 60;

const formatTimeLeft = (minutesLeft: number) => {
  const days = Math.floor(minutesLeft / (60 * 24));
  const hours = Math.floor((minutesLeft % (60 * 24)) / 60);
  const minutes = minutesLeft % 60;
  if (days) {
    return `${days}d ${hours}h`;
  }
  if (hours) {
    return `${hours}h ${minutes}m`;
  }
  return `${minutes}m`;
};

const ProvisionalExpiryPill = ({ validUntil }: { validUntil: string }) => {
  const [now, setNow] = useState(dayjs());

  useEffect(() => {
    const interval = setInterval(() => setNow(dayjs()), 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  if (!now.isBefore(validUntil)) {
    return (
      <Pill color="error" bg="error-extraLight">
        <AppText.Body2Medium color="error">
          {translate('supportRequests.provisionalBooking.expired')}
        </AppText.Body2Medium>
      </Pill>
    );
  }

  const minutesLeft = dayjs(validUntil).diff(now, 'minute');
  const isExpiringSoon = minutesLeft < PROVISIONAL_EXPIRY_WARNING_MINUTES;

  return (
    <Pill
      color={isExpiringSoon ? 'warning' : 'success'}
      bg={isExpiringSoon ? 'warning-extraLight' : 'success-extraLight'}
    >
      <AppText.Body2Medium color={isExpiringSoon ? 'warning-dark' : 'success-dark'}>
        {`${translate('supportRequests.provisionalBooking.expiresIn')} ${formatTimeLeft(minutesLeft)}`}
      </AppText.Body2Medium>
    </Pill>
  );
};

const AssignSelfButton = ({
  supportRequest,
  check,
}: {
  supportRequest: SupportRequestBackendClient;
  check: string;
}) => {
  const adminUser = useGetSupportItemAdminUser(supportRequest);

  const currentUserId = useGetAuthUser().currentUser?.uid;
  const [assignUser, { isLoading: isAssigning }] =
    supportRequestActionsApi.useAssignAdminUserSelfMutation();
  const [unassignUser, { isLoading: isUnassigning }] =
    supportRequestActionsApi.useUnassignAdminUserSelfMutation();
  const isOwn = adminUser === currentUserId;

  if (adminUser && !isOwn) {
    return null;
  }

  return (
    <Pressable
      onPress={() => {
        if (isOwn) {
          unassignUser({
            check,
            supportId: supportRequest._id,
          });
        } else {
          assignUser({
            check,
            supportId: supportRequest._id,
          });
        }
      }}
    >
      <Pill color={isOwn ? 'success' : 'monochrome'}>
        <HStack space="1">
          <IconButton
            color={isOwn ? 'success' : 'monochrome'}
            icon="customer-support-v2"
            size="small"
            isLoading={isAssigning || isUnassigning}
          />
          {isOwn ? (
            <AppText.MiscTag color="success">
              {translate('supportRequests.assigned')}
            </AppText.MiscTag>
          ) : (
            <AppText.MiscTag color="monochrome-dark">
              {translate('supportRequests.assignSelf')}
            </AppText.MiscTag>
          )}
        </HStack>
      </Pill>
    </Pressable>
  );
};

const ChangeTypeButton = ({ isLoading }: { isLoading?: boolean }) => {
  return (
    <MenuActionItem
      text={translate('supportRequests.changeType')}
      icon="edit"
      isLoading={isLoading}
      disabled={isLoading}
    />
  );
};

const DeleteButton = ({ isLoading }: { isLoading?: boolean }) => {
  return (
    <MenuActionItem
      text={translate('common.delete')}
      icon="bin"
      isLoading={isLoading}
      disabled={isLoading}
    />
  );
};

const ConversionTool = ({ isLoading }: { isLoading?: boolean }) => {
  return (
    <MenuActionItem
      text={translate('conversionTool.conversionTool')}
      icon="transactions"
      isLoading={isLoading}
      disabled={isLoading}
    />
  );
};

const DetailsButton = ({ isLoading }: { isLoading?: boolean }) => {
  return (
    <MenuActionItem
      text={translate('supportRequests.details')}
      icon="todo-list"
      isLoading={isLoading}
      disabled={isLoading}
    />
  );
};

export const SupportRequestItemHeaderSubTitle = ({
  email,
  reference,
  dateCreated,
  zendeskTicketNumber,
  trackId,
}: {
  email: string;
  reference: string;
  dateCreated: string;
  zendeskTicketNumber?: string;
  trackId?: string;
}) => {
  const navigate = useAppNavigation();
  const references = [zendeskTicketNumber, reference].filter(notEmpty);
  return (
    <SwitchStack space="1" style={{ overflow: 'hidden' }}>
      <Text.Body2Regular
        numberOfLines={1}
        color="monochrome-dark"
      >{`${references.join(' | ')} ${isWeb ? '|' : ''}`}</Text.Body2Regular>

      {trackId ? (
        <Text.Body2Regular numberOfLines={1} color="monochrome-dark">
          {`${trackId} ${isWeb ? '|' : ''}`}
        </Text.Body2Regular>
      ) : null}
      <LinkText
        onPress={() => {
          navigate.navigate('PurchasesByEmail', {
            email,
            tabTitle: email + ' - Purchases',
          });
        }}
      >{`${translate('supportRequests.seePurchasesByEmail', {
        email,
      })} ${isWeb ? '|' : ''}`}</LinkText>
      <LiveDateText
        dateTime={dateCreated}
        formatOption="fullDateTime"
        type="body2Regular"
        color="monochrome-dark"
      />
    </SwitchStack>
  );
};
